/**
 * practitioner-utils — Shared utility functions for practitioner hooks.
 *
 * Extracted to keep individual hook files under the 300-line limit.
 */

type Trend = 'improving' | 'stable' | 'worsening';

/**
 * Derive initials from a display name (e.g., "Dr. Sarah Chen" -> "SC").
 */
export function getInitials(name: string): string {
  const parts = name
    .replace(/^(Dr\.|Mr\.|Ms\.|Mrs\.)\s*/i, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (
    parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
  ).toUpperCase();
}

/**
 * Determine trend direction from a series of scores (chronological order).
 * Lower drama score = improving.
 */
export function deriveTrend(scores: number[]): Trend {
  if (scores.length < 2) return 'stable';
  const recent = scores.slice(-3);
  const older = scores.slice(0, -3);
  if (older.length === 0) return 'stable';
  const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
  const olderAvg = older.reduce((a, b) => a + b, 0) / older.length;
  const diff = recentAvg - olderAvg;
  if (diff < -0.5) return 'improving';
  if (diff > 0.5) return 'worsening';
  return 'stable';
}

/**
 * Format a date string as "MMM YYYY" (e.g., "Jan 2025").
 */
export function formatMemberSince(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}
